import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Award, MapPin, Flame, Clock, ExternalLink, Activity as ActivityIcon, Sparkles, Pencil, KeyRound } from "lucide-react";
import SEO from "@/components/SEO";
import { useAuth } from "@/contexts/AuthContext";
import EditProfileDialog from "@/components/EditProfileDialog";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import ProgressCharts from "@/components/ProgressCharts";

function paceLabel(p) {
  if (!p) return null;
  const m = Math.floor(p);
  const s = Math.round((p - m) * 60);
  return `${m}:${String(s).padStart(2, "0")} /km`;
}

function formatHMS(min) {
  if (min == null || min === "" || isNaN(min)) return null;
  const totalSec = Math.round(Number(min) * 60);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function Profile() {
  const { userId } = useParams();
  const { user: authUser } = useAuth();
  const [u, setU] = useState(null);
  const [activities, setActivities] = useState([]);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    api.get(`/users/${userId}`).then((r) => setU(r.data));
    api.get(`/activities/user/${userId}`).then((r) => setActivities(r.data.slice(0, 10))).catch(() => {});
    api.get(`/badges/catalog`).then((r) => setCatalog(r.data)).catch(() => {});
  }, [userId]);

  if (!u) return <div className="max-w-5xl mx-auto p-10 text-zinc-500 font-mono">Loading…</div>;

  const isOwn = authUser && authUser.user_id === u.user_id;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16" data-testid="profile-page">
      <SEO
        title={`${u.name} — Runner Profile`}
        path={`/profile/${userId}`}
        description={`${u.name}'s OSCR runner profile — ${u.total_km || 0} km logged, ${u.total_checkins || 0} check-ins, ${(u.badges || []).length} badges. ${u.bio ? u.bio.slice(0, 140) : 'Member of the Odisha Steel City Runners community.'}`}
      />

      {/* EDITORIAL HERO — trading-card feel */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end editorial-lift">
        <div className="md:col-span-4">
          <Avatar className="h-40 w-40 md:h-48 md:w-48 rounded-none avatar-editorial hairline">
            <AvatarImage src={u.picture} />
            <AvatarFallback className="rounded-none bg-zinc-800 text-white font-display text-5xl">
              {u.name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="md:col-span-8 min-w-0">
          <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">
            {u.city ? `${u.city.toUpperCase()} · ${u.role.toUpperCase()}` : u.role.toUpperCase()}
          </p>
          <h1 className="font-editorial text-5xl sm:text-6xl lg:text-7xl text-white mt-2 tracking-tight leading-[0.9]">
            {u.name}
          </h1>
          {u.bio && <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed">{u.bio}</p>}

          {isOwn && (
            <div className="flex flex-wrap gap-2 mt-6">
              <EditProfileDialog user={u} onUpdate={(updated) => setU((prev) => ({ ...prev, ...updated }))}>
                <Button
                  data-testid="edit-profile-btn"
                  variant="outline"
                  className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/10 font-display tracking-wider"
                >
                  <Pencil size={14} className="mr-2" /> EDIT PROFILE
                </Button>
              </EditProfileDialog>
              <ChangePasswordDialog>
                <Button
                  data-testid="change-password-btn"
                  variant="outline"
                  className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/10 font-display tracking-wider"
                >
                  <KeyRound size={14} className="mr-2" /> CHANGE PASSWORD
                </Button>
              </ChangePasswordDialog>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-14">
        <Stat icon={MapPin} label="TOTAL KM" value={u.total_km || 0} />
        <Stat icon={Flame} label="CHECK-INS" value={u.total_checkins || 0} />
        <Stat icon={Award} label="BADGES" value={(u.badges || []).length} />
        <Stat icon={Award} label="MEMBER SINCE" value={new Date(u.created_at).toLocaleDateString()} small />
      </div>

      {(u.badges || []).length > 0 && (
        <div className="mt-10" data-testid="profile-badges">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-display text-2xl text-white tracking-wider">BADGES EARNED</h2>
            <p className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
              {(u.badges || []).length} of {catalog.length || 9} unlocked
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {u.badges.map((b) => {
              const meta = catalog.find((c) => c.name.toLowerCase() === b.toLowerCase());
              const tier = meta?.tier || "honorary";
              const desc = meta?.desc;
              return (
                <div
                  key={b}
                  data-testid={`badge-${b.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`hairline p-4 transition group hover:-translate-y-0.5 ${
                    tier === "legend"
                      ? "bg-orange-600/10 border-orange-600/60"
                      : tier === "rare"
                      ? "bg-zinc-900 border-orange-600/40"
                      : "bg-zinc-900 border-zinc-800 hover:border-orange-600/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div
                      className={`w-9 h-9 hairline flex items-center justify-center transition group-hover:scale-110 ${
                        tier === "legend"
                          ? "bg-orange-600 border-orange-500"
                          : tier === "rare"
                          ? "bg-orange-600/20 border-orange-600/60"
                          : "bg-orange-600/10 border-orange-600/40"
                      }`}
                    >
                      {tier === "legend" ? (
                        <Sparkles className="text-white" size={16} />
                      ) : (
                        <span className="font-display text-orange-500 text-base">★</span>
                      )}
                    </div>
                    {tier === "legend" && (
                      <span className="font-mono text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5 bg-orange-600 text-white">LEGEND</span>
                    )}
                    {tier === "rare" && (
                      <span className="font-mono text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5 hairline border-orange-600/60 text-orange-500">RARE</span>
                    )}
                    {tier === "honorary" && (
                      <span className="font-mono text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5 hairline border-zinc-700 text-zinc-400">HONORARY</span>
                    )}
                  </div>
                  <h4 className="font-display text-sm text-white tracking-wider mt-3 uppercase">{b}</h4>
                  {desc && (
                    <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest mt-1">{desc}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ProgressCharts userId={userId} />

      {/* RECENT ACTIVITIES */}
      <div className="mt-12" data-testid="profile-recent-activities">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-2xl text-white tracking-wider">RECENT ACTIVITIES</h2>
          <p className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">Last 10 runs/walks</p>
        </div>
        {activities.length === 0 ? (
          <div className="hairline bg-zinc-900 p-8 text-center text-zinc-500 font-mono text-sm">
            No activities logged yet.
          </div>
        ) : (
          <div className="space-y-2">
            {activities.map((a) => (
              <div key={a.activity_id} className="hairline bg-zinc-900 p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-orange-500 uppercase">
                    {new Date(a.date).toDateString()}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 hairline bg-zinc-950 font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                    <ActivityIcon size={10} /> {a.activity_type}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-baseline gap-x-5 gap-y-1">
                  <span className="font-display text-2xl text-white">{a.distance_km || 0} <span className="text-sm text-zinc-500">KM</span></span>
                  {a.duration_min != null && (
                    <span className="font-mono text-xs text-zinc-300 flex items-center gap-1">
                      <Clock size={12} className="text-orange-500" /> {formatHMS(a.duration_min)}
                    </span>
                  )}
                  {a.pace_min_per_km && (
                    <span className="font-mono text-xs text-orange-500">{paceLabel(a.pace_min_per_km)}</span>
                  )}
                </div>
                {a.notes && <p className="text-sm text-zinc-400 mt-2">{a.notes}</p>}
                {a.strava_url && (
                  <a href={a.strava_url} target="_blank" rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs text-orange-500 hover:underline break-all">
                    <ExternalLink size={11} /> View on Strava
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, small }) {
  return (
    <div className="hairline bg-zinc-900/60 backdrop-blur-sm p-6 card-editorial">
      <Icon className="text-orange-500" size={16} />
      <div className={`font-mono ${small ? "text-sm" : "text-3xl"} text-white mt-4 tracking-tight font-semibold`}>{value}</div>
      <div className="font-mono text-[10px] text-zinc-500 tracking-[0.3em] mt-2 uppercase">{label}</div>
    </div>
  );
}
